import { Injectable } from '@angular/core';
import safeJsonStringify from 'safe-json-stringify';
import { WindowService } from '../../../../common/angular-shared/services/window/window.service';
import { FlexibleObj } from '../../../../common/js/interfaces/FlexibleObj';
import { BaseQuote } from '../../../../common/js/quote/BaseQuote';
import {
  QuotePageSearchSegmentProperties,
  CommissionChangeSegmentProperties,
  LinkSentSegmentProperties,
  SegmentEventType,
  VSTInteractedSegmentProperties,
  QuotePageClearFormSegmentProperties,
  GenericLinkActionSegmentProperties,
  DefaultAdvisorSiteSegmentProperties,
  SegmentEventAction,
  AdvisorQuoteToolSegmentProperties,
  ViewApplicationSegmentProperties,
  getSegmentEventAction,
  DuplicateApplicationSegmentProperties,
  AccountCenterInteractedSegmentProperties,
  ApplicationEditedSegmentProperties,
  ConceptIllustrationCalculatedSegmentProperties,
  ViewPolicySegmentProperties,
  AccountCenterInteractedChangeOfAddressSegmentProperties,
  AccountCenterInteractedChangeOfPaymentModeSegmentProperties,
  EmailSurveyResponseProperties,
} from './SegmentInterfaces';
import { Utils } from '../../../../common/js/Utils';
import { Application } from '../../../../common/js/customer/Application';
import { AmorphicService } from '../amorphic/amorphic.service';
import { SiteType } from '../../../js/interfaces/CoverpathTypes';
import { AgentSiteSegmentUtils } from './AgentSiteSegmentUtils';

@Injectable({
  providedIn: 'root',
})
export class SegmentService {
  private readonly sendToSegment: boolean;

  constructor(private windowService: WindowService, private amorphicService: AmorphicService) {
    this.sendToSegment = amorphicService.controller.sendToSegment;
    if (this.sendToSegment) {
      try {
        windowService.nativeWindow.analytics.load(amorphicService.controller.segmentKey);
      } catch (e) {
        // Treating segment exceptions as warnings
        this.amorphicService.amorphicLogWarn(
          'SegmentService',
          'constructor',
          'exception occurred while calling analytics.load().',
          Utils.toString(e)
        );
      }
    }
  }

  // identify lets you tie a user to their actions and record traits about them.
  // It includes a unique User ID and any optional traits you know about them
  // like their email, name, etc.
  identify(
    userId: string,
    traits: FlexibleObj<string | boolean | number>,
    options?: { integrations: { All?: boolean; Appcues?: boolean } }
  ) {
    try {
      this.windowService.nativeWindow.analytics.identify(userId, traits, options);
    } catch (e) {
      // Treating segment exceptions as warnings
      this.amorphicService.amorphicLogWarn(
        'SegmentService',
        'identify',
        `exception occurred while calling analytics.identify(), ${safeJsonStringify(traits)}.`,
        Utils.toString(e)
      );
    }
  }

  // The track API call is how you record any actions your users perform,
  // along with any properties that describe the action.
  track(event: SegmentEventType, properties: DefaultAdvisorSiteSegmentProperties) {
    try {
      this.windowService.nativeWindow.analytics.track(event, properties);
    } catch (e) {
      // Treating segment exceptions as warnings, catching them here so segment issues would not disrupt production services
      this.amorphicService.amorphicLogWarn(
        'SegmentService',
        'track',
        `exception occurred while calling analytics.track(), ${safeJsonStringify(properties)}.`,
        Utils.toString(e)
      );
    }
  }

  // The page call lets you record whenever a user sees a page of your website,
  // along with any optional properties about the page. Calling page or screen
  // in one of our sources is one of the first steps to getting started with Segment.
  page() {
    try {
      this.windowService.nativeWindow.analytics.page();
    } catch (e) {
      // Treating segment exceptions as warnings
      this.amorphicService.amorphicLogWarn(
        'SegmentService',
        'page',
        'exception occurred while calling analytics.page().',
        Utils.toString(e)
      );
    }
  }

  handleLinkSentEvent(
    application: Application,
    dividendInterestAdjustment: number,
    showPremiumToClient: boolean,
    isAdvisorEdit?: boolean
  ): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const sourceType = isAdvisorEdit ? 'advisorEdit' : 'sendAAA';
      const properties: LinkSentSegmentProperties = AgentSiteSegmentUtils.createLinkSentSegmentProperties(
        this.amorphicService.controller,
        application,
        sourceType,
        dividendInterestAdjustment,
        showPremiumToClient
      );
      this.track(SegmentEventType.LINK_SENT, properties);
    }
  }

  handleAdvisorQuoteToolUsedEvent(quote: BaseQuote, action: SegmentEventAction): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: AdvisorQuoteToolSegmentProperties =
        AgentSiteSegmentUtils.createAdvisorQuoteToolSegmentProperties(this.amorphicService.controller, quote, action);

      this.track(SegmentEventType.ADVISOR_QUOTE_TOOL_USED, properties);
    }
  }

  handleVstAgeChangedEvent(age: number): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: VSTInteractedSegmentProperties = AgentSiteSegmentUtils.createVSTInteractedSegmentProperties(
        this.amorphicService.controller,
        SegmentEventAction.AGE_SELECTED,
        String(age)
      );

      this.track(SegmentEventType.VST_INTERACTED, properties);
    }
  }

  handleVstDisplayStateChangedEvent(sourceOfAction: string, value: string): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: VSTInteractedSegmentProperties = AgentSiteSegmentUtils.createVSTInteractedSegmentProperties(
        this.amorphicService.controller,
        getSegmentEventAction(sourceOfAction),
        value
      );

      this.track(SegmentEventType.VST_INTERACTED, properties);
    }
  }

  handleCommissionSplitEvent(
    sourceOfAction: string,
    numOfAgents: number,
    fycPercentForPrimary: number,
    rycPercentForPrimary: number
  ): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: CommissionChangeSegmentProperties =
        AgentSiteSegmentUtils.createCommissionChangeSegmentProperties(
          this.amorphicService.controller,
          sourceOfAction,
          numOfAgents,
          fycPercentForPrimary,
          rycPercentForPrimary
        );

      this.track(SegmentEventType.COMMISSION_CHANGE, properties);
    }
  }

  handleQuotePageClearFormEvent(): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: QuotePageClearFormSegmentProperties =
        AgentSiteSegmentUtils.createQuotePageClearFormSegmentProperties(this.amorphicService.controller);

      this.track(SegmentEventType.CLEAR_FORM, properties);
    }
  }

  handleQuotePageSearchEvent(action: SegmentEventAction, customerId?: string): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: QuotePageSearchSegmentProperties = AgentSiteSegmentUtils.createQuotePageSearchSegmentProperties(
        this.amorphicService.controller,
        action,
        customerId
      );

      this.track(SegmentEventType.USE_SEARCH_FIELD, properties);
    }
  }

  handleGenericLinkActionEvent(action: SegmentEventAction): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: GenericLinkActionSegmentProperties =
        AgentSiteSegmentUtils.createGenericLinkActionSegmentProperties(this.amorphicService.controller, action);

      this.track(SegmentEventType.GENERIC_LINK_ACTION, properties);
    }
  }

  handleViewApplicationEvent(action: SegmentEventAction, source: string, applicationId?: string): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: ViewApplicationSegmentProperties = AgentSiteSegmentUtils.createViewApplicationSegmentProperties(
        this.amorphicService.controller,
        action,
        source,
        applicationId
      );

      this.track(SegmentEventType.VIEW_APPLICATION, properties);
    }
  }

  handleDuplicateAppEvent(
    action: SegmentEventAction,
    oldApplicationNumber: string,
    newApplicationNumber: string,
    dupLocation: string
  ) {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: DuplicateApplicationSegmentProperties =
        AgentSiteSegmentUtils.createDuplicateApplicationSegmentProperties(
          this.amorphicService.controller,
          action,
          oldApplicationNumber,
          newApplicationNumber,
          dupLocation
        );

      this.track(SegmentEventType.DUPLICATE_APPLICATION, properties);
    }
  }

  handleAccountCenterInteractedEvent(action: SegmentEventAction, value?: string): void {
    if (this.sendToSegment) {
      const properties: AccountCenterInteractedSegmentProperties =
        AgentSiteSegmentUtils.createAccountCenterInteractedSegmentProperties(
          this.amorphicService.controller,
          action,
          value
        );
      this.track(SegmentEventType.ACCOUNT_CENTER_INTERACTED, properties);
    }
  }

  handleApplicationEditedEvent(
    action: SegmentEventAction,
    sourceApplicationNumber: string,
    newApplicationNumber: string,
    location: string
  ) {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: ApplicationEditedSegmentProperties =
        AgentSiteSegmentUtils.createApplicationEditedSegmentProperties(
          this.amorphicService.controller,
          action,
          sourceApplicationNumber,
          newApplicationNumber,
          location
        );

      this.track(SegmentEventType.APPLICATION_EDITED, properties);
    }
  }

  handleDownloadSupplementalIllustration(product: string, isSupplemental: boolean) {
    if (this.sendToSegment) {
      const properties = AgentSiteSegmentUtils.createConceptIllustrationDownloadSupplementalIllustrationProperties(
        this.amorphicService.controller,
        SegmentEventAction.CONCEPT_ILLUSTRATION_DOWNLOAD_SUPPLEMENTAL_ILLUSTRATION,
        product,
        isSupplemental
      );

      this.track(SegmentEventType.CONCEPT_ILLUSTRATION_DOWNLOAD_SUPPLEMENTAL_ILLUSTRATION, properties);
    }
  }

  handleConceptIllustrationCalculateEvent(IllustrationInfo: ConceptIllustrationCalculatedSegmentProperties) {
    if (this.sendToSegment) {
      const properties = AgentSiteSegmentUtils.createConceptIllustrationCalculatedSegmentProperties(
        this.amorphicService.controller,
        SegmentEventAction.CONCEPT_ILLUSTRATION_CALCULATED,
        IllustrationInfo
      );

      this.track(SegmentEventType.CONCEPT_ILLUSTRATION_CALCULATED, properties);
    }
  }

  handleViewPolicyEvent(
    action: SegmentEventAction,
    source: string,
    channel: string,
    product: string,
    productType: string
  ): void {
    if (this.sendToSegment && this.amorphicService.controller.getPath() === SiteType.AGENT_SITE) {
      const properties: ViewPolicySegmentProperties = AgentSiteSegmentUtils.createViewPolicySegmentProperties(
        this.amorphicService.controller,
        action,
        source,
        channel,
        product,
        productType
      );

      this.track(SegmentEventType.VIEW_POLICY, properties);
    }
  }

  handleChangeOfPaymentFrequencyForPayByCheck(action: SegmentEventAction, currentPayment, targetFrequency): void {
    if (this.sendToSegment) {
      const properties: AccountCenterInteractedChangeOfPaymentModeSegmentProperties =
        AgentSiteSegmentUtils.createAccountCenterInteractedChangeOfPaymentModeSegmentProperties(
          this.amorphicService.controller,
          action,
          currentPayment,
          targetFrequency
        );
      this.track(SegmentEventType.ACCOUNT_CENTER_INTERACTED_CHANGEOFPAYMENTFREQUENCY, properties);
    }
  }

  handleChangeOfAddress(action: SegmentEventAction, isOwnerSameAsInsured: boolean): void {
    if (this.sendToSegment) {
      const properties: AccountCenterInteractedChangeOfAddressSegmentProperties =
        AgentSiteSegmentUtils.createAccountCenterInteractedChangeOfAddressSegmentProperties(
          this.amorphicService.controller,
          action,
          isOwnerSameAsInsured
        );
      this.track(SegmentEventType.ACCOUNT_CENTER_INTERACTED_CHANGEOFADDRESS, properties);
    }
  }

  handleEmailSurveyResponse(action: SegmentEventAction, emailKey, response): void {
    if (this.sendToSegment) {
      const properties: EmailSurveyResponseProperties =
        AgentSiteSegmentUtils.createIsHelpfulEmailSurveyResponseProperties(
          this.amorphicService.controller,
          action,
          emailKey,
          response
        );
      this.track(SegmentEventType.EMAIL_SURVEY_RESPONSE_RECORDED, properties);
    }
  }
}
